import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import useStyles from 'components/Pages/Admin/styles';
import FIDCAlert from 'components/Shared/FIDCAlert';
import PageLoader from 'components/Shared/PageLoader';
import { formatUrlWithUtm } from 'helpers/functions';
import useCustomObservable from 'hooks/useCustomObservable';
import authService from 'services/auth';
import { type User } from 'services/user/types';
import { BLINKET_TICKETS_APP_URL } from 'settings';

const AttendanceList = React.lazy(() => import('components/Pages/Admin/AttendanceList'));
const EventIndexPage = React.lazy(() => import('components/Pages/Admin/Event'));
const EventType = React.lazy(() => import('components/Pages/Admin/Event/EventType'));
const EventEdit = React.lazy(() => import('components/Pages/Admin/EventEdit'));
const EditByCntCod = React.lazy(() => import('components/Pages/Admin/EventEdit/EditByCntCod'));
const HelpCenterIndexPage = React.lazy(() => import('components/Pages/Admin/HelpCenter'));
const Home = React.lazy(() => import('components/Pages/Admin/Home'));
const MyEvents = React.lazy(() => import('components/Pages/Admin/MyEvents'));
const TabsMyEvents = React.lazy(() => import('components/Pages/Admin/TabsMyEvents'));
const EventCreate = React.lazy(() => import('Pages/Events/Create'));
const DuplicateEvent = React.lazy(() => import('Pages/Events/Duplicate'));
const EditEvent = React.lazy(() => import('Pages/Events/Edit'));
const Detail = React.lazy(() => import('Pages/Participants/Detail'));

const AuthenticatedRoutes = () => {
  const classes = useStyles();
  const { value: userData } = useCustomObservable<User, string, string>(() => authService.getUser(), null);

  if (!userData) {
    return <PageLoader />;
  }

  return (
    <div className={classes.content}>
      <FIDCAlert />
      <Switch>
        <Redirect exact from='/' to='/home' />

        <React.Suspense fallback={<PageLoader />}>
          <Route exact path='/home' component={Home} />
          <Route exact path='/help-center' component={HelpCenterIndexPage} />

          <Route exact path='/event' component={EventType} />
          <Route exact path='/event/create/:type' component={EventCreate} />

          <Route exact path='/event/edit/:id' component={EditEvent} />
          <Route exact path='/event-duplicate/:id' component={DuplicateEvent} />
          <Route exact path='/event/edit_by_cnt_cod/:cntCod' component={EditByCntCod} />

          <Route exact path='/my-events' component={MyEvents} />
          <Route exact path='/my-events/:id/edit' component={EventEdit} />

          <Route exact path='/my-events/:id/:page' component={TabsMyEvents} />

          <Route exact path='/my-events/:id/:page/:subPage' component={TabsMyEvents} />

          <Route
            exact
            path='/my-tickets'
            component={() => {
              const url = formatUrlWithUtm(BLINKET_TICKETS_APP_URL, 'blinket', 'produtor');
              window.location.href = url;
              return <Redirect to={url} />;
            }}
          />

          <Route exact path='/presence-list/:eventId' component={AttendanceList} />
          <Route exact path='/presence-list/:eventId/participant/:cliCod' component={Detail} />
        </React.Suspense>

        <Route exact path='*' component={Home} />
      </Switch>
    </div>
  );
};

export default AuthenticatedRoutes;
